// Physics — per-frame values matching original VA
export const PHYSICS = {
  PROJECTILE_GRAVITY: 0.15,
  GRAVITY: 0.06,
  MAX_FALL_SPEED: 4,
  ECB_RADIUS: 25,
  MAX_POWER: 28,
  CHARGE_RATE: 0.012,
  PROJECTILE_RADIUS: 5,
  BLAST_RADIUS: 60,
  GROUNDED_FRICTION: 0.85,     // non-tumble ground friction (tumble uses TUMBLE.GROUNDED_FRICTION)
  AIRBORNE_FRICTION: 0.95,     // non-tumble air friction (tumble uses TUMBLE.AIRBORNE_FRICTION)
  HORIZONTAL_STOP_THRESHOLD: 0.1,  // vx below this snaps to 0
  WORLD_BOUNCE_RETENTION: 0.9,      // velocity retention on world wall/ceiling bounce
  PROJECTILE_MIN_BOUNCE_VY: 3,      // min upward vy after terrain bounce (prevents dribble)
  BLAST_ZONE_BUFFER: 100,            // px below VIRTUAL_HEIGHT before declaring out_of_bounds
  GROUND_SNAP_TOLERANCE: 2,     // px tolerance for "is grounded" check
} as const;

// Touch slingshot wobble — tight oscillation around deflection-set power center
export const SLINGSHOT = {
  WOBBLE_AMPLITUDE: 0.08,  // ±8% power wobble around center
  WOBBLE_RATE: 0.004,      // per-frame wobble speed (~1.5 cycles/sec)
  RAMP_RATE: 0.012,        // per-frame ramp toward target (matches CHARGE_RATE — same time-to-max)
} as const;

// Surface normal slope classification
// |ny| = 1 → flat ground, |ny| = 0 → vertical wall.
// These thresholds define the physics vocabulary for "slope" vs "wall" vs "flat".
export const SLOPE = {
  WALL_NY: 0.35,           // |ny| ≤ this = wall (≥ ~70°). Bounce/block.
  FLAT_NY: 0.95,           // |ny| ≥ this = flat ground (≤ ~18°). Full friction.
  // Between WALL_NY and FLAT_NY = slope. Tangent projection, reduced friction.
} as const;

// Damage & knockback
export const DAMAGE = {
  KNOCKBACK_FORCE_MULT: 0.08,
} as const;

// Knockback percentage (Smash Bros-style)
export const KNOCKBACK = {
  KBG: 100,                   // knockback growth — force * (1 + dmg% / KBG)
  UPPER_BLAST_ZONE: -600,     // y above which = sky death (Melee: upper blast zone) — raised for more vertical recovery room
  TUMBLE_CANCEL_VX: 0.5,     // speed threshold for turn-advance settlement
  TUMBLE_CANCEL_VY: 0.5,
  BASE_LAUNCH_VY: 3.5,        // minimum upward pop for tumble hits (Smash-style)
} as const;

// Side death — open sides blast zones (tanks fly off and die)
// Set OPEN_SIDES to false to restore wall bounce behavior.
export const SIDE_DEATH = {
  OPEN_SIDES: true,           // true = tumble tanks fly off edges; false = pinball wall bounce
  BLAST_ZONE_OFFSET: 400,    // px beyond world edge (matches UPPER_BLAST_ZONE distance)
  FREEZE_MS: 400,
  SHAKE_INTENSITY: 50,
  FLASH_COLOR: 0xff8800,
  FLASH_ALPHA: 0.5,
  TEXT_COLOR: 0xff8800,
} as const;

// Hitlag — freeze + shake on explosion impact (Melee: both attacker/defender freeze)
export const HITLAG = {
  HITLAG_MS: 300,              // freeze duration on player hit
  TERRAIN_HITLAG_MS: 180,     // freeze duration on terrain-only hit
  HIT_SHAKE: 30,               // screen shake on player hit
  TERRAIN_SHAKE: 10,           // screen shake on terrain-only hit
  HIT_SHAKE_DECAY: 0.85,
  TERRAIN_SHAKE_DECAY: 0.82,
  SUB_HITLAG_MS: 80,              // player-hit hitstop for sub-projectiles (gen 1+)
  SUB_FLASH_ALPHA: 0.06,          // subtle single flash for sub-projectiles
  SUB_TERRAIN_DEBRIS_CHANCE: 0.4, // probability of terrain debris per sub-explosion
} as const;
